// v10/src/components/admin/AdminHourPackages.js
//
// PR #6 — Paquetes de horas (saldo por alumno)
//
// Vista de admin/instructor para:
//   - Ver saldos por alumno (destacando los negativos)
//   - Cargar un paquete nuevo (alumno + horas + vencimiento opcional + nota)
//   - Ver el historial de paquetes (y cancelarlos si fue carga errónea)
//
// Patrón visual similar al resto del admin (tarjetas + modal). NO usa toasts
// externos: feedback inline arriba de la lista.

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, Plus, X, Search, AlertTriangle, Calendar, History,
  Trash2, User, RefreshCw, CheckCircle,
} from 'lucide-react';
import api from '../../services/apiService';

// ────────────────────────────────────────────────────────────────────
// Modal de creación de paquete
// ────────────────────────────────────────────────────────────────────
const CreatePackageModal = ({ darkMode, students, defaultStudentId, onClose, onCreated }) => {
  const [studentId, setStudentId]     = useState(defaultStudentId || '');
  const [hours, setHours]             = useState('');
  const [expiresAt, setExpiresAt]     = useState('');
  const [note, setNote]               = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const h = parseFloat(hours);
    if (!studentId)              return setError('Seleccioná un alumno');
    if (isNaN(h) || h <= 0)      return setError('Las horas deben ser un número mayor a 0');

    setSaving(true);
    try {
      await api.post('/api/hour-packages', {
        student_user_id: studentId,
        hours_purchased: h,
        expires_at:      expiresAt || null,
        note:            note || null,
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al crear paquete');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelClass = `block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`;

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:80, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <motion.form
        initial={{ scale:0.96, y:12 }} animate={{ scale:1, y:0 }}
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className={`${darkMode?'bg-gray-800':'bg-white'} w-full max-w-md rounded-2xl shadow-2xl overflow-hidden`}>

        <div className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9' }}>
          <h3 className={`text-lg font-bold ${darkMode?'text-white':'text-gray-900'}`}>
            Cargar paquete de horas
          </h3>
          <button type="button" onClick={onClose}
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${darkMode?'hover:bg-gray-700 text-gray-400':'hover:bg-gray-100 text-gray-500'}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className={labelClass}>Alumno</label>
            <select value={studentId} onChange={e => setStudentId(e.target.value)}
              className={inputClass} required>
              <option value="">— Elegí un alumno —</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Horas compradas</label>
            <input type="number" step="0.5" min="0.1"
              value={hours} onChange={e => setHours(e.target.value)}
              placeholder="Ej: 10"
              className={inputClass} required />
          </div>

          <div>
            <label className={labelClass}>
              Fecha de vencimiento <span className="font-normal text-xs opacity-70">(opcional)</span>
            </label>
            <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
              className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>
              Nota <span className="font-normal text-xs opacity-70">(opcional)</span>
            </label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Ej: pagó por transferencia"
              className={inputClass} />
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl text-sm font-medium"
              style={{ background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex gap-3 justify-end"
          style={{ borderColor: darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9' }}>
          <button type="button" onClick={onClose} disabled={saving}
            className={`px-4 py-2 rounded-xl font-semibold ${darkMode?'text-gray-300 hover:bg-gray-700':'text-gray-700 hover:bg-gray-100'}`}>
            Cancelar
          </button>
          <button type="submit" disabled={saving}
            className="px-5 py-2 rounded-xl font-semibold text-white"
            style={{ background:'linear-gradient(135deg,#16a34a,#15803d)', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Guardando…' : 'Cargar paquete'}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Modal: historial de paquetes de un alumno
// ────────────────────────────────────────────────────────────────────
const StudentPackagesModal = ({ darkMode, student, userRole, onClose, onCancelPackage }) => {
  const [list, setList]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/hour-packages?student_user_id=${student.student_user_id}&include_inactive=true`);
      setList(r.data?.data || []);
    } catch (_) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [student.student_user_id]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (pkg) => {
    if (!window.confirm(`¿Cancelar el paquete de ${pkg.hours_purchased} hs?`)) return;
    setBusyId(pkg.id);
    try {
      await api.delete(`/api/hour-packages/${pkg.id}`);
      await load();
      onCancelPackage?.();
    } catch (e) {
      window.alert(e?.response?.data?.message || 'No se pudo cancelar');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:80, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <motion.div
        initial={{ scale:0.96, y:12 }} animate={{ scale:1, y:0 }}
        onClick={e => e.stopPropagation()}
        className={`${darkMode?'bg-gray-800':'bg-white'} w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
        style={{ maxHeight:'85vh' }}>

        <div className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9' }}>
          <div>
            <h3 className={`text-lg font-bold ${darkMode?'text-white':'text-gray-900'}`}>
              {student.student_name}
            </h3>
            <p className={`text-xs mt-0.5 ${darkMode?'text-gray-400':'text-gray-500'}`}>
              {student.student_email}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${darkMode?'hover:bg-gray-700 text-gray-400':'hover:bg-gray-100 text-gray-500'}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-sm opacity-60">Cargando…</div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 text-sm opacity-60">Sin paquetes</div>
          ) : (
            <div className="space-y-2">
              {list.map(p => {
                const isExpired  = p.is_expired;
                const isInactive = !p.is_active;
                const balanceColor = p.balance < 0 ? '#ef4444' : p.balance === 0 ? '#f59e0b' : '#16a34a';
                return (
                  <div key={p.id} className="rounded-xl p-4 flex items-center gap-3"
                    style={{
                      background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                      opacity: isInactive ? 0.5 : 1,
                    }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-bold text-base ${darkMode?'text-white':'text-gray-900'}`}>
                          {p.hours_purchased} hs
                        </span>
                        <span className="text-xs opacity-70">→ usadas {p.hours_consumed} hs</span>
                        {isExpired && !isInactive && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background:'rgba(245,158,11,0.15)', color:'#d97706' }}>
                            VENCIDO
                          </span>
                        )}
                        {isInactive && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background:'rgba(148,163,184,0.15)', color:'#64748b' }}>
                            CANCELADO
                          </span>
                        )}
                      </div>
                      <div className="text-xs opacity-60 mt-1 flex gap-3 flex-wrap">
                        <span>📅 {new Date(p.created_at).toLocaleDateString('es-AR')}</span>
                        {p.expires_at && <span>⏰ vence {new Date(p.expires_at).toLocaleDateString('es-AR')}</span>}
                        {p.created_by_name && <span>👤 {p.created_by_name}</span>}
                      </div>
                      {p.note && (
                        <div className="text-xs italic opacity-70 mt-1">"{p.note}"</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xl font-black leading-none" style={{ color: balanceColor }}>
                        {p.balance}
                      </div>
                      <div className="text-[10px] font-semibold opacity-60">hs saldo</div>
                    </div>
                    {!isInactive && userRole === 'admin' && (
                      <button onClick={() => cancel(p)} disabled={busyId === p.id}
                        title="Cancelar paquete"
                        className={`p-2 rounded-lg ${darkMode?'hover:bg-gray-700 text-red-400':'hover:bg-red-50 text-red-600'}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Componente principal
// ────────────────────────────────────────────────────────────────────
const AdminHourPackages = ({ darkMode, userRole }) => {
  const [balances, setBalances] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaultStudent, setCreateDefaultStudent] = useState('');
  const [detailStudent, setDetailStudent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [balRes, usersRes] = await Promise.all([
        api.get('/api/hour-packages/balances'),
        api.get('/api/users'),
      ]);
      setBalances(balRes.data?.data || []);
      const allUsers = usersRes.data?.data || usersRes.data || [];
      setStudents(allUsers.filter(u => u.role === 'pilot' && u.is_active !== false));
    } catch (_) {
      setBalances([]);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Merge: alumnos con paquetes + alumnos sin paquete (saldo 0)
  const merged = (() => {
    const byId = new Map();
    balances.forEach(b => byId.set(b.student_user_id, { ...b }));
    students.forEach(s => {
      if (!byId.has(s.id)) {
        byId.set(s.id, {
          student_user_id: s.id,
          student_name:    s.name,
          student_email:   s.email,
          packages_count:  0,
          total_balance:   0,
          active_balance:  0,
          last_package_at: null,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => {
      if ((a.total_balance < 0) !== (b.total_balance < 0)) return a.total_balance < 0 ? -1 : 1;
      return a.student_name.localeCompare(b.student_name);
    });
  })();

  const filtered = merged.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.student_name?.toLowerCase().includes(q) || s.student_email?.toLowerCase().includes(q);
  });

  const totalNegative = merged.filter(s => s.total_balance < 0).length;

  const cardBase = `rounded-2xl border p-4 transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode?'text-white':'text-gray-900'} flex items-center gap-2`}>
            <Wallet className="w-6 h-6 text-emerald-500" />
            Paquetes de horas
          </h2>
          <p className={`text-sm mt-1 ${darkMode?'text-gray-400':'text-gray-600'}`}>
            Saldo de horas por alumno. Al cerrar un vuelo con pago "Paquete", se descuenta automáticamente.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className={`p-2 rounded-xl ${darkMode?'bg-gray-700 hover:bg-gray-600 text-gray-200':'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'}`}
            title="Recargar">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setCreateDefaultStudent(''); setShowCreate(true); }}
            className="px-4 py-2 rounded-xl font-semibold text-white flex items-center gap-2"
            style={{ background:'linear-gradient(135deg,#16a34a,#15803d)' }}>
            <Plus className="w-4 h-4" /> Cargar paquete
          </button>
        </div>
      </div>

      {/* Alert de saldos negativos */}
      {totalNegative > 0 && (
        <div className="rounded-2xl p-4 flex items-center gap-3"
          style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)' }}>
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          <div className="text-sm">
            <span className="font-bold text-red-500">{totalNegative}</span>
            <span className={darkMode?'text-gray-200':'text-gray-700'}>
              {' '}{totalNegative === 1 ? 'alumno con saldo negativo' : 'alumnos con saldo negativo'}
            </span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={`${cardBase} flex flex-col sm:flex-row gap-3`}>
        <div className="flex-1 relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode?'text-gray-400':'text-gray-500'}`} />
          <input type="text" placeholder="Buscar alumno…"
            value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-xl border ${darkMode?'bg-gray-700 border-gray-600 text-white':'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-emerald-500`} />
        </div>
      </div>

      {/* Listado */}
      {loading ? (
        <div className={`${cardBase} text-center py-16`}>
          <p className={`text-sm ${darkMode?'text-gray-400':'text-gray-600'}`}>Cargando…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className={`${cardBase} text-center py-16`}>
          <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className={`text-sm ${darkMode?'text-gray-400':'text-gray-600'}`}>Sin alumnos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(s => {
            const isNeg = s.total_balance < 0;
            const isZero = s.total_balance === 0;
            const color = isNeg ? '#ef4444' : isZero ? '#94a3b8' : '#16a34a';
            const bg    = isNeg ? 'rgba(239,68,68,0.08)' : 'transparent';
            return (
              <motion.div key={s.student_user_id}
                initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                onClick={() => setDetailStudent(s)}
                className={`${cardBase} cursor-pointer hover:shadow-md`}
                style={{ background: bg || undefined,
                         borderColor: isNeg ? 'rgba(239,68,68,0.35)' : undefined }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${color}20`, color }}>
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm truncate ${darkMode?'text-white':'text-gray-900'}`}>
                      {s.student_name}
                    </p>
                    <p className={`text-xs truncate ${darkMode?'text-gray-400':'text-gray-500'}`}>
                      {s.student_email}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div>
                    <p className="text-3xl font-black leading-none" style={{ color }}>
                      {s.total_balance}
                    </p>
                    <p className="text-[10px] font-semibold opacity-60 mt-1">hs disponibles</p>
                  </div>
                  <div className="text-right text-[11px] opacity-70">
                    {s.packages_count > 0 ? (
                      <>
                        <p>{s.packages_count} {s.packages_count === 1 ? 'paquete' : 'paquetes'}</p>
                        {s.active_balance !== s.total_balance && (
                          <p className="opacity-70">activo: {s.active_balance} hs</p>
                        )}
                      </>
                    ) : (
                      <p className="italic">sin paquetes</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={(e) => { e.stopPropagation(); setCreateDefaultStudent(s.student_user_id); setShowCreate(true); }}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1
                      ${darkMode?'bg-gray-700 hover:bg-gray-600 text-emerald-300':'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'}`}>
                    <Plus className="w-3 h-3" /> Paquete
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDetailStudent(s); }}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1
                      ${darkMode?'bg-gray-700 hover:bg-gray-600 text-gray-200':'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}>
                    <History className="w-3 h-3" /> Historial
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modales */}
      <AnimatePresence>
        {showCreate && (
          <CreatePackageModal
            darkMode={darkMode}
            students={students}
            defaultStudentId={createDefaultStudent}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); load(); }}
          />
        )}
        {detailStudent && (
          <StudentPackagesModal
            darkMode={darkMode}
            student={detailStudent}
            userRole={userRole}
            onClose={() => setDetailStudent(null)}
            onCancelPackage={load}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminHourPackages;
